import { AdminApiError, type AdminClient, type BigWinnerOverride } from '@apx/admin-sdk';
import { prettyGame } from '../games.js';
import { askPrompt } from '../modal.js';
import { fmtDateTime, fmtNumber } from '../format.js';
import { bigWinnerSchema } from '@apx/integrator-config/schemas';
import { showToast } from '../toast.js';

export interface BigWinnerPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
}

const DEFAULT_VALUE: BigWinnerOverride = { threshold: '10000000' };

export async function renderBigWinnerPage(
  root: HTMLElement,
  deps: BigWinnerPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Big-Winner Threshold — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Payouts greater than or equal to this threshold trigger a broadcast on <code>round.settled</code> events. The player-facing marquee subscribes and renders these in real time.</p>

    <div class="card">
      <h2>Threshold</h2>
      <p class="muted" style="margin-top:-4px">One threshold for every currency, compared with each payout as stored: an integer of the payout's smallest unit, so 10,000,000 is 10,000,000 rupiah and 100,000.00 US dollars.</p>
      <div class="row">
        <label style="flex:1">Threshold
          <input id="bw-threshold" type="number" min="0" step="100" />
        </label>
        <button id="bw-save" class="primary">Propose change</button>
        <button id="bw-reset" class="ghost">Reset to legacy default (10,000,000)</button>
      </div>
      <p id="bw-msg" class="muted"></p>
    </div>

    <div class="card">
      <h2>Preview impact</h2>
      <p class="muted" style="margin-top:-4px">Count of settled bets in the lookback window whose payout would have triggered the broadcast at the proposed threshold.</p>
      <div class="row">
        <label>Window
          <select id="bw-since">
            <option value="1h">Last hour</option>
            <option value="24h" selected>Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </label>
        <button id="bw-preview">Preview impact</button>
      </div>
      <div id="bw-preview-result" style="margin-top:14px"></div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="bw-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const thresholdEl = root.querySelector<HTMLInputElement>('#bw-threshold')!;
  const msgEl = root.querySelector<HTMLElement>('#bw-msg')!;
  const previewResultEl = root.querySelector<HTMLElement>('#bw-preview-result')!;
  const sinceEl = root.querySelector<HTMLSelectElement>('#bw-since')!;

  function fillForm(value: BigWinnerOverride): void {
    thresholdEl.value = value.threshold;
  }

  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'bigWinner');
  fillForm((current as BigWinnerOverride | null) ?? DEFAULT_VALUE);

  root.querySelector<HTMLButtonElement>('#bw-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    msgEl.textContent = 'Reset to legacy default (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#bw-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const value: BigWinnerOverride = { threshold: thresholdEl.value };
    const parsed = bigWinnerSchema.safeParse(value);
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => e.message).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'bigWinner',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  root.querySelector<HTMLButtonElement>('#bw-preview')!.addEventListener('click', async () => {
    previewResultEl.innerHTML = '<p class="muted">Running…</p>';
    try {
      const preview = await deps.client.previewBigWinner(deps.integratorId, deps.gameId, {
        since: sinceEl.value as '1h' | '24h' | '7d' | '30d',
        threshold: thresholdEl.value,
      });
      previewResultEl.innerHTML = `
        <div class="kv">
          <dt>Would have fired</dt><dd style="color:var(--accent);font-size:20px;font-weight:600">${preview.wouldHaveFired}</dd>
          <dt>Window</dt><dd>${preview.since}</dd>
          <dt>Threshold</dt><dd>${fmtNumber(preview.threshold)}</dd>
        </div>
        ${preview.topMatches.length > 0 ? `<table style="margin-top:14px">
          <thead><tr><th>When</th><th>Player</th><th>Payout</th><th>Round</th></tr></thead>
          <tbody>${preview.topMatches
            .map(
              (m) => `<tr>
                <td>${m.settledAt ? fmtDateTime(m.settledAt) : '—'}</td>
                <td><code>${m.playerId.slice(0, 8)}…</code></td>
                <td>${fmtNumber(m.payout)}</td>
                <td><code>${m.roundId.slice(-8)}</code></td>
              </tr>`,
            )
            .join('')}</tbody>
        </table>` : ''}
      `;
    } catch (err) {
      previewResultEl.innerHTML = `<p class="error">${
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err)
      }</p>`;
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'bigWinner' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#bw-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Pending</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td><code>${JSON.stringify(a.pendingConfig)}</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'bigWinner');
          fillForm((fresh.value as BigWinnerOverride | null) ?? DEFAULT_VALUE);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }
  await refreshPending();
}
